import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getInvoicesByUser } from "../actions/invoiceActions";

const currencyFmt = (value, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const dateFmt = (value) => (value ? new Date(value).toLocaleDateString() : "No date");

const computeOutstanding = (invoice = {}) => {
  const total = Number(invoice.total ?? 0);
  const explicitReceived = Number(invoice.totalAmountReceived ?? 0);
  if (explicitReceived) return Math.max(total - explicitReceived, 0);
  const payments = Array.isArray(invoice.paymentRecords)
    ? invoice.paymentRecords.reduce((sum, rec) => sum + Number(rec?.amountPaid || 0), 0)
    : 0;
  return Math.max(total - payments, 0);
};

const normalizeAmount = (value) => {
  const parsed = Number(String(value ?? "").replace(/[$,]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const isReceivedByToday = (invoice = {}) => {
  if (!invoice.receivedByDate) return false;
  const receivedDate = new Date(invoice.receivedByDate);
  if (Number.isNaN(receivedDate.getTime())) return false;
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  return receivedDate <= todayEnd;
};

export default function OutstandingInvoicesPage() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { invoices = [], isLoading = false } = useSelector((state) => state?.invoices || {});
  const [query, setQuery] = React.useState("");
  const [savedAmounts, setSavedAmounts] = React.useState({});

  const userInfo = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      return stored?.result || stored;
    } catch {
      return null;
    }
  }, []);

  const storageKey = React.useMemo(() => {
    const userKey = userInfo?._id || userInfo?.googleId || userInfo?.email || "anonymous";
    return `outstandingCustomerAmounts:${userKey}`;
  }, [userInfo]);

  React.useEffect(() => {
    const id = userInfo?._id || userInfo?.googleId;
    if (!id || invoices?.length) return;
    dispatch(getInvoicesByUser({ search: id }));
  }, [dispatch, invoices?.length, userInfo]);

  React.useEffect(() => {
    try {
      setSavedAmounts(JSON.parse(localStorage.getItem(storageKey) || "{}"));
    } catch {
      setSavedAmounts({});
    }
  }, [storageKey]);

  const saveAmount = (customer, value) => {
    setSavedAmounts((prev) => {
      const next = { ...prev, [customer]: value };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const groups = React.useMemo(() => {
    const grouped = new Map();
    (invoices || []).forEach((invoice) => {
      if (!isReceivedByToday(invoice)) return;
      const amountDue = computeOutstanding(invoice);
      if (amountDue <= 0) return;
      const status = String(invoice?.status || "").toLowerCase();
      if (!["unpaid", "partial", "pending"].includes(status)) return;

      const customer =
        invoice?.billTo?.name ||
        invoice?.client?.store ||
        invoice?.client?.name ||
        "Unknown customer";
      if (!grouped.has(customer)) {
        grouped.set(customer, { customer, total: 0, invoices: [] });
      }
      const group = grouped.get(customer);
      group.total += amountDue;
      group.invoices.push({
        id: invoice._id,
        invoiceNumber: invoice.invoiceNumber,
        receivedByDate: invoice.receivedByDate,
        dueDate: invoice.dueDate,
        amountDue,
      });
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        invoices: group.invoices.sort(
          (a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0)
        ),
      }))
      .filter((group) => group.customer.toLowerCase().includes(query.trim().toLowerCase()))
      .sort((a, b) => b.total - a.total);
  }, [invoices, query]);

  const totals = React.useMemo(() => {
    const outstanding = groups.reduce((sum, group) => sum + group.total, 0);
    const saved = groups.reduce((sum, group) => sum + normalizeAmount(savedAmounts[group.customer]), 0);
    const invoiceCount = groups.reduce((sum, group) => sum + group.invoices.length, 0);
    return { outstanding, saved, invoiceCount, difference: saved - outstanding };
  }, [groups, savedAmounts]);

  return (
    <Container maxWidth="lg" style={{ paddingTop: 24, paddingBottom: 80 }}>
      <Box mb={3}>
        <Typography variant="h4" style={{ fontWeight: 700 }}>
          Outstanding Invoices by Customer
        </Typography>
      </Box>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="textSecondary">Outstanding</Typography>
              <Typography variant="h6">{currencyFmt(totals.outstanding)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="textSecondary">Saved Amounts</Typography>
              <Typography variant="h6">{currencyFmt(totals.saved)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="textSecondary">Difference</Typography>
              <Typography variant="h6" style={{ color: Math.abs(totals.difference) < 0.01 ? "#047857" : "#b45309" }}>
                {currencyFmt(totals.difference)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="textSecondary">Invoices</Typography>
              <Typography variant="h6">{totals.invoiceCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mb={2}>
        <TextField
          label="Filter customers"
          variant="outlined"
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          fullWidth
        />
      </Box>

      {isLoading ? (
        <Typography>Loading invoices...</Typography>
      ) : groups.length === 0 ? (
        <Typography>No outstanding invoices found.</Typography>
      ) : (
        <Grid container spacing={2}>
          {groups.map((group) => {
            const savedValue = savedAmounts[group.customer] || "";
            const savedNumber = normalizeAmount(savedValue);
            const difference = savedValue === "" ? null : savedNumber - group.total;
            return (
              <Grid item xs={12} key={group.customer}>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                          {group.customer}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {group.invoices.length} outstanding invoice{group.invoices.length === 1 ? "" : "s"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="textSecondary">Outstanding</Typography>
                        <Typography variant="body1" style={{ fontWeight: 700 }}>
                          {currencyFmt(group.total)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          label="Saved amount"
                          type="number"
                          variant="outlined"
                          size="small"
                          value={savedValue}
                          onChange={(event) => saveAmount(group.customer, event.target.value)}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="textSecondary">Difference</Typography>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 700, color: difference === null || Math.abs(difference) < 0.01 ? "#047857" : "#b45309" }}
                        >
                          {difference === null ? "-" : currencyFmt(difference)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Box mt={2} style={{ borderTop: "1px solid #e5e7eb" }}>
                      {group.invoices.map((invoice) => (
                        <Box
                          key={invoice.id}
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          py={1}
                          style={{ borderBottom: "1px solid #f3f4f6", gap: 12 }}
                        >
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 700 }}>
                              Invoice #{invoice.invoiceNumber || invoice.id}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Ship Date {dateFmt(invoice.receivedByDate)} - Due {dateFmt(invoice.dueDate)}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Typography variant="body2" style={{ fontWeight: 700 }}>
                              {currencyFmt(invoice.amountDue)}
                            </Typography>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => history.push(`/invoice/${invoice.id}`)}
                              startIcon={<LaunchIcon />}
                            >
                              Open
                            </Button>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
}
